import { api } from "@/api/client";

export const vaultSecurityApi = {
  // Canonical server-side vault authorization gate (booleans only, no contents)
  access: () => api.get("/vault-security/access").then((r) => r.data),

  // Phase 2 — hardened vault session · retry lockout
  sessionPinUnlock: (pin, mode = "pin") => api.post("/vault-security/session/pin-unlock", { pin, mode }).then((r) => r.data),
  sessionOpen: () => api.post("/vault-security/session/open").then((r) => r.data),
  sessionHeartbeat: (session_id, active = true) => api.post("/vault-security/session/heartbeat", { session_id, active }).then((r) => r.data),
  sessionClose: (session_id) => api.post("/vault-security/session/close", { session_id }).then((r) => r.data),
  sessionLockout: () => api.get("/vault-security/session/lockout").then((r) => r.data),

  // Phase 3 — founder-only vault activity log (read-only, display-safe)
  activityLog: (limit = 25, skip = 0) => api.get(`/vault-security/activity-log?limit=${limit}&skip=${skip}`).then((r) => r.data),

  // Setup v2 — universal setup-first vault (all users, min 3 methods)
  setupStatus: () => api.get("/vault-security/setup-status").then((r) => r.data),
  setupComplete: () => api.post("/vault-security/setup-complete").then((r) => r.data),
  pinSet: (pin) => api.post("/vault-security/security/pin-set", { pin }).then((r) => r.data),

  // iteration_28 — biometric + TOTP staged security
  profile: () => api.get("/vault-security/profile").then((r) => r.data.profile),
  bioEnroll: (payload) => api.post("/vault-security/biometric/enroll", { payload }).then((r) => r.data),
  bioChallenge: (payload) => api.post("/vault-security/biometric/challenge", { payload }).then((r) => r.data),
  bioDisable: () => api.post("/vault-security/biometric/disable").then((r) => r.data),
  totpInit: () => api.post("/vault-security/totp/setup-init").then((r) => r.data),
  totpConfirm: (code) => api.post("/vault-security/totp/setup-confirm", { code }).then((r) => r.data),
  totpChallenge: (code) => api.post("/vault-security/totp/challenge", { code }).then((r) => r.data),
  totpDisable: () => api.post("/vault-security/totp/disable").then((r) => r.data),

  // Layer 7A — onboarding, gate credentials, recovery
  onboard: (body) => api.post("/vault-security/onboard", body).then((r) => r.data),
  gateConfig: () => api.get("/vault-security/gate-config").then((r) => r.data.gate),
  gateWipe: () => api.post("/vault-security/gate/wipe").then((r) => r.data),
  recoveryQuestion: () => api.get("/vault-security/recovery-question").then((r) => r.data),
  recover: (body) => api.post("/vault-security/recover", body).then((r) => r.data),
  complianceManifest: () => api.get("/vault-security/compliance/manifest").then((r) => r.data),

  // Layer 7A — BYOK keys shelf
  byokList: () => api.get("/vault-security/byok").then((r) => r.data.keys),
  byokSet: (provider, key) => api.post("/vault-security/byok/set", { provider, key }).then((r) => r.data),
  byokWipe: (provider) => api.post("/vault-security/byok/wipe", { provider }).then((r) => r.data),

  // Layer 7A — brand + tax repositories
  repoList: (repo) => api.get(`/vault-security/repo/${repo}`).then((r) => r.data.files),
  repoUpload: (repo, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/vault-security/repo/${repo}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data.file);
  },
  repoDelete: (repo, id) => api.post(`/vault-security/repo/${repo}/delete`, { id }).then((r) => r.data),
  taxSyncInbox: () => api.post("/vault-security/repo/tax/sync-inbox").then((r) => r.data),
  taxScrapeReceipts: () => api.post("/vault-security/repo/tax/scrape-receipts").then((r) => r.data),

  // ===== Stage 7B =====
  // C2 — extensible schema-agnostic BYOK key-value registry
  byokRegistry: () => api.get("/vault-security/byok-registry").then((r) => r.data.keys),
  byokRegSet: (name, value) => api.post("/vault-security/byok-registry/set", { name, value }).then((r) => r.data.keys),
  byokRegWipe: (name) => api.post("/vault-security/byok-registry/wipe", { name }).then((r) => r.data.keys),
  byokRegReveal: (name) => api.post("/vault-security/byok-registry/reveal", { name }).then((r) => r.data.value),

  // C3 — tax inbox scraper + founder cash-flow ledger
  taxConnectInbox: (email) => api.post("/vault-security/repo/tax/connect-inbox", { email }).then((r) => r.data),
  taxReceipts: () => api.get("/vault-security/repo/tax/receipts").then((r) => r.data),
  cashflow: () => api.get("/vault-security/cashflow").then((r) => r.data),
  cashflowCreds: (body) => api.post("/vault-security/cashflow/creds", body).then((r) => r.data),

  // C4 — security override desk
  setSecurityQuestion: (security_question, security_answer) => api.post("/vault-security/security/question", { security_question, security_answer }).then((r) => r.data),
  pinChange: (current_pin, new_pin) => api.post("/vault-security/security/pin-change", { current_pin, new_pin }).then((r) => r.data),

  // C5 — multi-asset canvas
  web3List: () => api.get("/vault-security/assets/web3").then((r) => r.data.assets),
  web3Add: (body) => api.post("/vault-security/assets/web3", body).then((r) => r.data.asset),
  web3Update: (body) => api.post("/vault-security/assets/web3/update", body).then((r) => r.data.asset),
  web3Delete: (id) => api.post("/vault-security/assets/web3/delete", { id }).then((r) => r.data),
  // M2 — encrypted recovery locker (opaque cipher only)
  web3LockerSet: (id, locker) => api.post("/vault-security/assets/web3/locker/set", { id, locker }).then((r) => r.data),
  web3LockerClear: (id) => api.post("/vault-security/assets/web3/locker/clear", { id }).then((r) => r.data),
  web3LockerGet: (id) => api.get(`/vault-security/assets/web3/${id}/locker`).then((r) => r.data.locker),
  // M3 — Sub-Coin Tax Isolation Matrix (client-encrypted; opaque cipher only)
  web3SubCoinsSet: (id, cipher, count) => api.post("/vault-security/assets/web3/sub-coins/set", { id, cipher, count }).then((r) => r.data),
  web3SubCoinsClear: (id) => api.post("/vault-security/assets/web3/sub-coins/clear", { id }).then((r) => r.data),
  web3SubCoinsGet: (id) => api.get(`/vault-security/assets/web3/${id}/sub-coins`).then((r) => r.data),
  stockList: () => api.get("/vault-security/assets/stock").then((r) => r.data.assets),
  stockAdd: (body) => api.post("/vault-security/assets/stock", body).then((r) => r.data.asset),
  stockDelete: (id) => api.post("/vault-security/assets/stock/delete", { id }).then((r) => r.data),
  networthCanvas: () => api.get("/vault-security/networth-canvas").then((r) => r.data.canvas),

  // C6 — master admin desk (founder-only)
  adminQueue: () => api.get("/vault-security/admin/identity-queue").then((r) => r.data.queue),
  adminApprove: (user_id) => api.post("/vault-security/admin/identity/approve", { user_id }).then((r) => r.data),
  adminReject: (user_id) => api.post("/vault-security/admin/identity/reject", { user_id }).then((r) => r.data),
  adminForceClear: (email) => api.post("/vault-security/admin/force-clear", { email }).then((r) => r.data),
  adminDiagnostics: () => api.get("/vault-security/admin/diagnostics").then((r) => r.data.diagnostics),

  // C7 — hidden government document vault (founder-only)
  govAccess: () => api.get("/vault-security/gov-docs/access").then((r) => r.data),
  govList: () => api.get("/vault-security/gov-docs").then((r) => r.data.docs),
  govAdd: (body) => api.post("/vault-security/gov-docs", body).then((r) => r.data.doc),
  govReveal: (id) => api.post("/vault-security/gov-docs/reveal", { id }).then((r) => r.data),
  govWipe: (id) => api.post("/vault-security/gov-docs/wipe", { id }).then((r) => r.data),
  govUploadImage: (docId, side, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/vault-security/gov-docs/${docId}/image/${side}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },

  // C1/C3 — founder landing-page reel remote control
  landingReel: () => api.get("/vault-security/landing-reel").then((r) => r.data),
  landingReelUpload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/vault-security/landing-reel/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  landingReelClear: () => api.post("/vault-security/landing-reel/clear").then((r) => r.data),
  landingReelGenerate: () => api.post("/vault-security/landing-reel/generate").then((r) => r.data),

  // BYOK Multi-Model Synthesis — Prompt-to-Blueprint pipeline
  blueprintTranscribe: (file) => {
    const fd = new FormData();
    fd.append("file", file, file.name || "dictation.webm");
    return api.post("/vault-security/blueprint/transcribe", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  blueprintParse: (text) => api.post("/vault-security/blueprint/parse", { text }).then((r) => r.data),
  blueprintRender: (blueprint, shot) => api.post("/vault-security/blueprint/render", { blueprint, shot }).then((r) => r.data),
  blueprintAnimate: (body) => api.post("/vault-security/blueprint/animate", body).then((r) => r.data),
  blueprintGallery: () => api.get("/vault-security/blueprint/gallery").then((r) => r.data.assets),
  blueprintPushLanding: (asset_id) => api.post("/vault-security/blueprint/push-landing", { asset_id }).then((r) => r.data),

  // Phase 2 — Gamification loop (daily streak + milestone spin-wheel)
  gamification: () => api.get("/vault-security/gamification").then((r) => r.data.gamification),
  gamifyCheckin: () => api.post("/vault-security/gamification/checkin").then((r) => r.data),
  gamifySpin: () => api.post("/vault-security/gamification/spin").then((r) => r.data),
  gamifyClassification: (classification) => api.post("/vault-security/gamification/classification", { classification }).then((r) => r.data.gamification),

  // Stage 9 — M-Token redemption sink
  gamifyRedemptions: () => api.get("/vault-security/gamification/redemptions").then((r) => r.data),
  gamifyRedeem: (reward) => api.post("/vault-security/gamification/redeem", { reward }).then((r) => r.data),

  // Stage 9 — live keyless Solana web3 scraper
  web3Scan: (address) => api.post("/vault-security/assets/web3/scan", { address }).then((r) => r.data.scan),
  web3ScanStored: (id) => api.get(`/vault-security/assets/web3/${id}/scan`).then((r) => r.data),

  // Stage 9 — blueprint dual-gate + live tax intake webhook
  blueprintGate: () => api.get("/vault-security/blueprint/gate").then((r) => r.data),
  taxWebhookUrl: () => api.get("/vault-security/tax/webhook-url").then((r) => r.data),
};

// Stream a blueprint asset (image or mp4) as an authenticated blob URL.
export const streamBlueprintAsset = async (id) => {
  const res = await api.get(`/vault-security/blueprint/asset/${id}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// Stream a founder gov-doc image as an authenticated blob URL.
export const streamGovImage = async (id) => {
  const res = await api.get(`/vault-security/gov-docs/image/${id}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// Stream a repo file as an authenticated blob URL (image thumbnails).
export const streamRepoFile = async (id) => {
  const res = await api.get(`/vault-security/repo/file/${id}`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};

// Deterministic biometric-array signature (mockup payload). A live camera scan
// re-derives the same stable device/identity signature so the same node passes.
export async function bioSignature(userId) {
  const enc = new TextEncoder().encode(`misteck-bio-array::${userId || "anon"}`);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}
